import { getVvsGotchiMoleProfilePath, Path } from 'config/constants/paths'
import { useTranslation } from 'contexts/Localization'
import { groupBy } from 'lodash'
import React, { Dispatch, SetStateAction, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useDraggable } from 'react-use-draggable-scroll'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { Mole } from 'state/vvsgotchi/models/mole'
import CheckBox from 'views/VvsGotchi/components/CheckBox'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { tierToIcon, tierToIconSize, MAX_TEAM_SIZE, rarityToName } from 'views/VvsGotchi/config'
import { Text, Flex, UserIcon, useMatchBreakpoints } from 'vvs-uikit'
import { MoleCard } from 'views/VvsGotchi/components/MoleCard'
import { AddMoleButton, MoleAvatarBottomBadgeWrapper, MoleAvatar, Carousel } from './styles'

export const ExpeditionTeamMiniRow = () => {
  const { team } = useGotchiOwner()
  const history = useHistory()
  return (
    <Carousel my={2} gap="0.75rem">
      {team &&
        team.map((t) => {
          const badgeSrc = tierToIcon[t.tier]
          return (
            <MoleAvatar size="56px" rounded key={t.tokenId} image={t.imageUrl}>
              {badgeSrc && (
                <MoleAvatarBottomBadgeWrapper>
                  <PixelatedImage src={badgeSrc} width={tierToIconSize[t.tier].sm} />
                </MoleAvatarBottomBadgeWrapper>
              )}
            </MoleAvatar>
          )
        })}
      {(!team || team.length < MAX_TEAM_SIZE) && (
        <AddMoleButton onClick={() => history.push(Path.VVS_GOTCHI_TEAM_SELECT)}>
          <Text bold color="success" fontSize="28px">
            +
          </Text>
        </AddMoleButton>
      )}
    </Carousel>
  )
}

export const ExpeditionTeamRow = () => {
  const { team } = useGotchiOwner()
  const { t } = useTranslation()

  if (!team || team.length <= 0) {
    return (
      <Flex height="271.5px" flexDirection="column" justifyContent="center" alignItems="center">
        <UserIcon />
        <Text color="black">{t('Please add Moles to team')}</Text>
      </Flex>
    )
  }

  return <ExpeditionTeamCarousel team={team} />
}

const ExpeditionTeamCarousel = ({ team }: { team: Mole[] }) => {
  const draggableMolesRowRef = useRef()
  const { events } = useDraggable(draggableMolesRowRef)
  const history = useHistory()

  return (
    <Carousel {...events} ref={draggableMolesRowRef}>
      {team &&
        team.map((mole) => {
          return (
            <MoleCard
              key={mole.tokenId}
              mole={mole}
              onClick={() => history.push(getVvsGotchiMoleProfilePath(mole?.tokenId?.toString()))}
            />
          )
        })}
    </Carousel>
  )
}

interface ExpeditionTeamSelectCheckRowProps {
  selectedIds: Array<number> | undefined
  setSelectedIds: Dispatch<SetStateAction<number[]>>
}
export const ExpeditionTeamSelectCheckRow: React.FC<ExpeditionTeamSelectCheckRowProps> = ({
  selectedIds,
  setSelectedIds,
}) => {
  const { t } = useTranslation()
  const { isMobile } = useMatchBreakpoints()
  const { moles } = useGotchiOwner()
  const handleSelectMole = useCallback(
    (id: number) => {
      if (selectedIds.includes(id) || selectedIds.length >= MAX_TEAM_SIZE)
        setSelectedIds(selectedIds.filter((m) => m !== id))
      else setSelectedIds([...selectedIds, id])
    },
    [setSelectedIds, selectedIds],
  )

  const molesByRarity = groupBy(moles, (m) => m.rarity)

  return (
    <Flex my={2} gap={isMobile ? '0.5rem' : '1rem'} flexWrap="wrap">
      {Object.keys(molesByRarity).map((rarity) => {
        const moleGroup = molesByRarity[rarity]
        return moleGroup
          .map((m) => [m, selectedIds?.includes(m.tokenId) && m.isLocked] as const)
          .map(([m, selectLocked], i) => (
            <React.Fragment key={m.tokenId}>
              {i === 0 && (
                <Text width="100%" fontSize="13px" color="darkestNightBlue">
                  {rarityToName[m.rarity]?.(t)}
                </Text>
              )}
              <Flex
                key={m.tokenId}
                flexDirection="column"
                onClick={() => {
                  if (selectLocked) return
                  handleSelectMole(m.tokenId)
                }}
              >
                <MoleCard key={m.tokenId} mole={m} disabled={selectLocked} />
                <Flex justifyContent="center" mt={3}>
                  <CheckBox checked={selectedIds?.includes(m.tokenId)} disabled={selectLocked} />
                </Flex>
              </Flex>
            </React.Fragment>
          ))
      })}
    </Flex>
  )
}
